/**
 * FL-132: デモ体験用時系列データ管理
 *
 * 2025年7月22日（火曜日）を基準とした時系列デモシナリオの設計
 * - 過去データ: 2025年6月22日 〜 2025年7月21日
 * - 当日データ: 2025年7月22日（デモ実行日）
 * - 未来データ: 2025年7月23日 〜 2025年8月22日
 */

import { isValidDate } from '../../constants/dates';

/**
 * VITE_FIXED_DATEまたはフォールバック日付を取得
 */
const getFixedDate = (): string => {
  const fixedDate = import.meta.env.VITE_FIXED_DATE;
  return fixedDate && isValidDate(fixedDate) ? fixedDate : '2025-07-22';
};

// デモ基準日付情報
export const DEMO_TIMELINE = {
  // 基準日（デモ当日）
  BASE_DATE: getFixedDate(), // VITE_FIXED_DATEまたは'2025-07-22'
  DAY_OF_WEEK: '火曜日',

  // 過去期間（1ヶ月前から前日まで）
  PAST_PERIOD: {
    START: '2025-06-22', // 1ヶ月前
    END: '2025-07-21', // 前日
  },

  // 未来期間（翌日から1ヶ月後まで）
  FUTURE_PERIOD: {
    START: '2025-07-23', // 翌日
    END: '2025-08-22', // 1ヶ月後
  },

  // 週間情報（7/22を含む週）
  CURRENT_WEEK: {
    MONDAY: '2025-07-21',
    TUESDAY: '2025-07-22', // デモ当日
    WEDNESDAY: '2025-07-23',
    THURSDAY: '2025-07-24',
    FRIDAY: '2025-07-25',
    SATURDAY: '2025-07-26',
    SUNDAY: '2025-07-27',
  },

  // 前週情報
  PREVIOUS_WEEK: {
    MONDAY: '2025-07-14',
    TUESDAY: '2025-07-15',
    WEDNESDAY: '2025-07-16',
    THURSDAY: '2025-07-17',
    FRIDAY: '2025-07-18',
    SATURDAY: '2025-07-19',
    SUNDAY: '2025-07-20',
  },

  // 翌週情報
  NEXT_WEEK: {
    MONDAY: '2025-07-28',
    TUESDAY: '2025-07-29',
    WEDNESDAY: '2025-07-30',
    THURSDAY: '2025-07-31',
    FRIDAY: '2025-08-01',
    SATURDAY: '2025-08-02',
    SUNDAY: '2025-08-03',
  },
} as const;

// デモシナリオのステージ定義
export const DEMO_STAGES = {
  // 過去完了期間（実績データ）
  HISTORICAL: {
    name: '過去実績',
    period: DEMO_TIMELINE.PAST_PERIOD,
    description: '完了済み案件の履歴データ',
    caseStatuses: ['completed'] as const,
    schedulesStatus: 'completed' as const,
  },

  // 当日進行中（デモメイン）
  TODAY: {
    name: '当日業務',
    period: { START: getFixedDate(), END: getFixedDate() },
    description: 'デモ体験の中心となる当日の業務データ',
    caseStatuses: ['unassigned', 'Assigned', 'in_transit'] as const,
    schedulesStatus: 'active' as const,
  },

  // 近未来予定（スケジューリング）
  SCHEDULED: {
    name: '予定業務',
    period: DEMO_TIMELINE.FUTURE_PERIOD,
    description: '今後の予定案件とスケジュール',
    caseStatuses: ['unassigned'] as const,
    schedulesStatus: 'scheduled' as const,
  },
} as const;

// 火曜日の業務特性
export const TUESDAY_BUSINESS_CHARACTERISTICS = {
  // 火曜日の典型的な業務パターン
  TYPICAL_PATTERNS: {
    // 月曜のフォローアップ
    mondayFollowUp: true,
    // 週中の効率的業務
    midWeekEfficiency: true,
    // 定期作業の実行
    regularOperations: true,
  },

  // 火曜日の時間帯別特徴
  TIME_PATTERNS: {
    MORNING: {
      timeRange: { start: '08:00', end: '12:00' },
      characteristics: ['月曜日のフォローアップ', '通常業務の実行', '翌日以降の準備'],
    },
    AFTERNOON: {
      timeRange: { start: '13:00', end: '17:00' },
      characteristics: ['効率的な業務運営', '週中の定期作業', '進捗管理と調整'],
    },
    EVENING: {
      timeRange: { start: '17:00', end: '20:00' },
      characteristics: ['当日業務の完了', '翌日の事前準備', '緊急対応サポート'],
    },
  },
} as const;

// デモデータの時系列分布設計
export const DEMO_DATA_DISTRIBUTION = {
  // 案件数の日別分布
  CASE_DISTRIBUTION: {
    // 過去1ヶ月（平均的な分布）
    HISTORICAL_DAILY: 3, // 1日平均3件
    // 当日（デモ用に多め）
    TODAY_TOTAL: 8, // 火曜日は多めに設定
    // 未来1ヶ月（スケジューリング用）
    FUTURE_DAILY: 2, // 1日平均2件（予定）
  },

  // 陸送員の稼働分布
  TRANSPORTER_DISTRIBUTION: {
    // 稼働中陸送員数（火曜日）
    ACTIVE_TODAY: 6,
    // 休暇・非稼働
    OFF_TODAY: 2,
    // 夜勤シフト
    NIGHT_SHIFT: 1,
  },

  // 時間帯別案件分布（7/22）
  TIME_DISTRIBUTION: {
    MORNING: 3, // 08:00-12:00
    AFTERNOON: 3, // 13:00-17:00
    EVENING: 2, // 17:00-20:00
  },
} as const;

// ユーティリティ関数
export const timelineUtils = {
  /**
   * 指定日付がどのステージに属するかを判定
   */
  getStageForDate(dateString: string): keyof typeof DEMO_STAGES {
    const fixedDate = getFixedDate();
    if (dateString === fixedDate) {
      return 'TODAY';
    }
    if (dateString < fixedDate) {
      return 'HISTORICAL';
    }
    return 'SCHEDULED';
  },

  /**
   * 指定日付が当週かどうかを判定
   */
  isCurrentWeek(dateString: string): boolean {
    return (
      dateString >= DEMO_TIMELINE.CURRENT_WEEK.MONDAY &&
      dateString <= DEMO_TIMELINE.CURRENT_WEEK.SUNDAY
    );
  },

  /**
   * 火曜日の時間帯特性を取得
   */
  getTuesdayTimeCharacteristics(
    time: string
  ):
    | (typeof TUESDAY_BUSINESS_CHARACTERISTICS.TIME_PATTERNS)[keyof typeof TUESDAY_BUSINESS_CHARACTERISTICS.TIME_PATTERNS]
    | null {
    const hour = parseInt(time.split(':')[0]);

    if (hour >= 8 && hour < 12) {
      return TUESDAY_BUSINESS_CHARACTERISTICS.TIME_PATTERNS.MORNING;
    }
    if (hour >= 13 && hour < 17) {
      return TUESDAY_BUSINESS_CHARACTERISTICS.TIME_PATTERNS.AFTERNOON;
    }
    if (hour >= 17 && hour < 20) {
      return TUESDAY_BUSINESS_CHARACTERISTICS.TIME_PATTERNS.EVENING;
    }

    return null;
  },

  /**
   * デモリセット時の基準日付を取得
   */
  getResetDate(): string {
    return getFixedDate();
  },
};

export default DEMO_TIMELINE;
